/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { AssetTranscript, AssetTranscriptWord } from "types/Asset";
import { BlockToken } from "types/Block";

export const tokensAreContiguous = (tokens: BlockToken[]): boolean => {
  if (tokens.length < 2) return true;

  let previousToken: BlockToken | null = null;
  const contiguous = tokens.every((token): boolean => {
    const contiguous =
      previousToken === null ||
      (token.transcriptSegmentIndex === previousToken.transcriptSegmentIndex &&
        previousToken.segmentWordIndex !== null &&
        token.segmentWordIndex === previousToken.segmentWordIndex + 1);
    previousToken = token;
    return contiguous;
  });

  return contiguous;
};

export const editTranscriptFromTokens = (
  transcript: AssetTranscript | undefined,
  tokens: BlockToken[],
  newText: string
): AssetTranscript | null => {
  const firstToken = tokens[0];
  const lastToken = tokens[tokens.length - 1];

  if (
    !transcript ||
    !newText ||
    firstToken.transcriptSegmentIndex === null ||
    firstToken.segmentWordIndex === null ||
    lastToken.segmentWordIndex === null
  ) {
    return null;
  }

  if (tokens.length === 1) {
    return editTranscriptWord(
      transcript,
      firstToken.transcriptSegmentIndex,
      firstToken.segmentWordIndex,
      newText
    );
  } else if (tokens.length > 1) {
    return coalesceTranscriptWordRange(
      transcript,
      firstToken.transcriptSegmentIndex,
      firstToken.segmentWordIndex,
      lastToken.segmentWordIndex,
      newText
    );
  } else {
    return null;
  }
};

export const editTranscriptDisplayName = (
  transcript: AssetTranscript,
  unidentifiedSpeakerNumber: number,
  displayName: string
): AssetTranscript => {
  return transcript.map((segment) => {
    return segment.unidentifiedSpeakerNumber === unidentifiedSpeakerNumber
      ? { ...segment, displayName: displayName, displayNameIsOverridden: true }
      : segment;
  });
};

export const editTranscriptWord = (
  transcript: AssetTranscript,
  segmentIndexToUpdate: number,
  wordIndexToUpdate: number,
  newText: string
): AssetTranscript | null => {
  if (transcript[segmentIndexToUpdate]?.words[wordIndexToUpdate]) {
    const segment = transcript[segmentIndexToUpdate];
    const newWords = segment.words.map((word, wordIndex) => {
      return wordIndex === wordIndexToUpdate
        ? { ...word, text: newText }
        : word;
    });
    const newSegmentText = newWords.map((word) => word.text).join(" ");

    return transcript.map((segment, segmentIndex) => {
      return segmentIndex === segmentIndexToUpdate
        ? {
            ...segment,
            words: newWords,
            text: newSegmentText,
          }
        : segment;
    });
  } else {
    return null;
  }
};

export const coalesceTranscriptWordRange = (
  transcript: AssetTranscript,
  segmentIndexToUpdate: number,
  firstWordIndex: number,
  lastWordIndex: number,
  newText: string
): AssetTranscript | null => {
  if (firstWordIndex > lastWordIndex) {
    return null;
  }

  const segment = transcript[segmentIndexToUpdate];
  const words = segment?.words;
  const firstWord = words?.[firstWordIndex];
  const lastWord = words?.[lastWordIndex];

  if (words && firstWord && lastWord) {
    const newWord: AssetTranscriptWord = {
      startTime: firstWord.startTime,
      endTime: lastWord.endTime,
      text: newText,
      id: lastWord.id,
    };

    const newWords = [
      words.slice(0, firstWordIndex),
      newWord,
      words.slice(lastWordIndex + 1),
    ].flat();

    const newSegmentText = newWords.map((word) => word.text).join(" ");

    return transcript.map((segment, segmentIndex) => {
      return segmentIndex === segmentIndexToUpdate
        ? { ...segment, words: newWords, text: newSegmentText }
        : segment;
    });
  } else {
    return null;
  }
};
